import React, { useState, useEffect, memo } from 'react';
import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { ThemeProvider } from '@mui/material/styles';
import { createTheme } from '@mui/material';
import { Button, Form } from '../ui-kit';
import { IconXmark } from '../ui-kit/icons';
import { themeHash } from '../ui-kit/helpers';
import moment from 'moment';

const DateTimePicker = ({
  name,
  value,
  onChange,
  disabled,
  minDateTime,
  maxDateTime,
  customLabel,
  showClearButton,
  customThemeOverrides,
  allowOpenEnded,
  minutesInterval,
  dateOnly
}) => {
  const [previousValue, setPreviousValue] = useState(value);

  // Both the DatePicker and TimePicker fire onChange on every keystroke
  // while the user is typing (even for incomplete/invalid intermediate
  // input, e.g. a year of "202" while typing "2026"). Propagating those
  // immediately to the parent causes flicker and can corrupt the other
  // (date/time) part of the value. Instead, keep track of the in-progress
  // value locally and only propagate a valid value via onChange once the
  // user is done editing (on blur), or immediately when a value is picked
  // via the picker UI (onAccept).
  const [pendingDateValue, setPendingDateValue] = useState(null);
  const [pendingTimeValue, setPendingTimeValue] = useState(null);

  if (typeof minDateTime === 'string') minDateTime = moment(minDateTime);
  if (typeof maxDateTime === 'string') maxDateTime = moment(maxDateTime);

  // The TimePicker component only validates the time part of two datetimes.
  // To avoid false positives, if the min date is before the given date,
  // we will compare against 00:00
  // False positive - 15:00 (13-12-2030) < 17:00 (12-12-2030)
  // Correct - 15:00 (13-12-2030) > 00:00 (12-12-2030)
  let minTime = minDateTime && moment(minDateTime);
  if (minTime?.isBefore(value)) minTime = minTime.startOf('day');

  // Or if the given date is after the max date, we will compare against 23:59
  // False positive - 15:00 (12-12-2030) > 12:00 (13-12-2030)
  // Correct - 15:00 (12-12-2030) < 23:59 (13-12-2030)
  let maxTime = maxDateTime && moment(maxDateTime);
  if (maxTime?.isAfter(value)) maxTime = maxTime.endOf('day');

  let label = customLabel === undefined ? (name === 'startTime' ? I18n.t('from') : I18n.t('to')) : customLabel;

  // If we have no value, don't prefill with current time. We need declare
  // value as null, because undefined would cause a controlled/uncontrolled
  // input warning
  let valueMomentized = value && moment(value).isValid() ? moment(value) : null;
  if (valueMomentized && dateOnly) valueMomentized = valueMomentized.startOf('day');

  // Reset the pending (not-yet-committed) values whenever the external
  // value changes, so the fields stay in sync with the parent.
  useEffect(() => {
    setPendingDateValue(null);
    setPendingTimeValue(null);
  }, [value]);

  const dateValue = pendingDateValue === null ? valueMomentized : pendingDateValue;
  const timeValue = pendingTimeValue === null ? valueMomentized : pendingTimeValue;

  const handleDateChange = newValue => {
    setPendingDateValue(newValue);
  };

  const handleTimeChange = newValue => {
    setPendingTimeValue(newValue);
  };

  const commitDateChange = () => {
    if (pendingDateValue !== null && moment(pendingDateValue).isValid()) onChange(pendingDateValue);
  };

  const commitTimeChange = () => {
    if (pendingTimeValue !== null && moment(pendingTimeValue).isValid()) onChange(pendingTimeValue);
  };

  const setOpenEnded = value => {
    if (value) {
      // store the previous value so when open ended is turned off, we get the
      // original value back
      setPreviousValue(valueMomentized && valueMomentized.format());
      onChange(null);
    } else {
      onChange(previousValue);
    }
  };

  const customThemeHash = { ...themeHash };
  customThemeHash.components.MuiOutlinedInput = {
    ...customThemeOverrides,
    ...customThemeHash.components.MuiOutlinedInput
  };
  const customTheme = createTheme(customThemeHash);

  return (
    <ThemeProvider theme={customTheme}>
      <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={I18n.locale}>
        <DatePicker
          disabled={disabled}
          label={label}
          value={dateValue}
          minDate={minDateTime}
          maxDate={maxDateTime}
          inputFormat={'DD/MM/YYYY'}
          onChange={handleDateChange}
          onAccept={onChange}
          slotProps={{ textField: { onBlur: commitDateChange } }}
        />{' '}
        {dateOnly || (
          <TimePicker
            sx={{ width: '110px' }}
            ampm={false}
            disabled={disabled}
            value={timeValue}
            onChange={handleTimeChange}
            onAccept={onChange}
            minTime={minTime}
            maxTime={maxTime}
            desktopModeMediaQuery="" // never show the mobile analog picker
            slotProps={{ textField: { onBlur: commitTimeChange } }}
            timeSteps={{ minutes: minutesInterval || 1 }}
          />
        )}
      </LocalizationProvider>{' '}
      {showClearButton && (
        <Button variant="outline-secondary" onClick={() => onChange(null)}>
          <IconXmark />
        </Button>
      )}
      {allowOpenEnded && (
        <Form.Check
          id="setOpenEnded"
          onChange={e => setOpenEnded(e.target.checked)}
          checked={value === null}
          label={I18n.t('open_ended')}
        />
      )}
    </ThemeProvider>
  );
};

export default memo(DateTimePicker);
